// SPDX-FileCopyrightText: 2025 Slant Tech LLC
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * API service for communicating with the KiCad Review App backend
 */

const API_BASE = '/api';

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Check if the error indicates the user needs to re-authenticate
 * This can happen when:
 * - Session expired
 * - GitLab token was revoked
 * - GitLab permissions changed
 */
function shouldForceReauth(status, errorData) {
  if (status === 401) {
    // Check for specific GitLab token invalid signal from backend
    if (errorData?.detail === 'gitlab_token_invalid' || errorData?.action === 'reauthenticate') {
      return true;
    }
    // Generic 401 also triggers reauth
    return true;
  }
  // 403 with reauth action (token-level permission issues)
  if (status === 403 && errorData?.action === 'reauthenticate') {
    return true;
  }
  return false;
}

/**
 * Force the user to re-authenticate by redirecting to login
 * Preserves the current URL so they return here after login
 */
function forceReauthenticate(returnUrl = null) {
  // Clear any cached auth state
  sessionStorage.removeItem('user');
  sessionStorage.removeItem('authChecked');
  
  // Build return URL - preserve current location so user returns here
  const currentPath = returnUrl || window.location.pathname + window.location.search;
  const encodedReturn = encodeURIComponent(currentPath);
  
  // Redirect to login with return URL
  window.location.href = `${API_BASE}/auth/login?redirect=${encodedReturn}`;
}

async function request(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include', // Include cookies for session auth
    ...options,
  };

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  const response = await fetch(url, config);

  // Try to parse error data for better handling
  let errorData = {};
  if (!response.ok) {
    try {
      errorData = await response.clone().json();
    } catch {
      errorData = { detail: response.statusText };
    }
  }

  // Handle authentication errors - check if we need to force re-login
  if (shouldForceReauth(response.status, errorData)) {
    // Log for debugging
    console.warn('Authentication error detected, forcing re-login:', {
      status: response.status,
      detail: errorData?.detail,
      action: errorData?.action
    });
    
    // If the error explicitly says to reauthenticate, do it immediately
    if (errorData?.action === 'reauthenticate' || errorData?.detail === 'gitlab_token_invalid') {
      forceReauthenticate();
      // Return a rejected promise so the calling code knows something happened
      throw new ApiError(
        errorData?.message || 'Your session has expired. Redirecting to login...',
        response.status,
        { ...errorData, requiresAuth: true, redirecting: true }
      );
    }
    
    // For generic 401s, throw error but let the app decide what to do
    throw new ApiError(
      'Authentication required',
      401,
      { ...errorData, requiresAuth: true }
    );
  }

  if (!response.ok) {
    throw new ApiError(
      errorData.detail || 'Request failed',
      response.status,
      errorData
    );
  }

  // Handle empty responses
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

export const api = {
  // Authentication
  async getAuthStatus() {
    return request('/auth/status');
  },

  async getCurrentUser() {
    return request('/auth/user');
  },

  getLoginUrl() {
    return `${API_BASE}/auth/login`;
  },

  async logout() {
    return request('/auth/logout', { method: 'POST' });
  },

  // Health & Config
  async getHealth() {
    return request('/health');
  },

  async getConfig() {
    return request('/config');
  },

  // Dashboard - get user's MRs with KiCad files
  async getDashboardMergeRequests() {
    return request('/dashboard/merge-requests');
  },

  // Merge Request
  async getMergeRequest(mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    return request(`/merge-request?${params}`);
  },

  // KiCad Files
  async getKicadFiles(mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    return request(`/kicad-files?${params}`);
  },

  // Sheet hierarchy for hierarchical schematics
  async getSheetHierarchy(filePath, mrIid, projectId) {
    return request(`/review/${projectId}/${mrIid}/sheet-hierarchy/${encodeURIComponent(filePath)}`);
  },

  // File content URL (for KiCanvas)
  getFileUrl(filePath, ref = null, mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    if (ref) params.append('ref', ref);
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    // Don't encode the file path - it's handled by the browser/server
    // The :path FastAPI parameter handles URL-decoded paths
    return `${API_BASE}/file/${filePath}?${params}`;
  },

  // File info (bounds, components) for coordinate mapping
  async getFileInfo(filePath, mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    return request(`/file-info/${encodeURIComponent(filePath)}?${params}`);
  },

  // Get diff between source and target branches for a file
  async getFileDiff(filePath, mrIid, projectId) {
    return request(`/review/${projectId}/${mrIid}/diff/${encodeURIComponent(filePath)}`);
  },

  // Comments
  async getComments(filePath = null, mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    if (filePath) params.append('file_path', filePath);
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    return request(`/comments?${params}`);
  },

  async createComment(commentData, syncToGitlab = true, mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    params.append('sync_to_gitlab', syncToGitlab);
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    return request(`/comments?${params}`, {
      method: 'POST',
      body: commentData,
    });
  },

  async updateComment(commentId, updateData, syncToGitlab = true) {
    const params = new URLSearchParams();
    params.append('sync_to_gitlab', syncToGitlab);
    return request(`/comments/${commentId}?${params}`, {
      method: 'PUT',
      body: updateData,
    });
  },

  async deleteComment(commentId, syncToGitlab = true) {
    const params = new URLSearchParams();
    params.append('sync_to_gitlab', syncToGitlab);
    return request(`/comments/${commentId}?${params}`, {
      method: 'DELETE',
    });
  },

  // Sync
  async syncFromGitlab(mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    return request(`/sync-from-gitlab?${params}`, {
      method: 'POST',
    });
  },

  async cleanup(mrIid = null, projectId = null) {
    const params = new URLSearchParams();
    if (mrIid) params.append('mr_iid', mrIid);
    if (projectId) params.append('project_id', projectId);
    return request(`/cleanup?${params}`, {
      method: 'POST',
    });
  },

  /**
   * Force re-authentication by redirecting to login
   * Useful when components detect auth issues
   */
  forceReauthenticate(returnUrl = null) {
    forceReauthenticate(returnUrl);
  },

  /**
   * Check if an error requires re-authentication
   */
  isAuthError(error) {
    if (error instanceof ApiError) {
      return error.data?.requiresAuth || error.status === 401;
    }
    return false;
  },
};

export { ApiError, forceReauthenticate };
export default api;
